import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PurchaseService } from '../../../services/purchaseService';
import { formatCurrency } from '../../../utils/formatters';

const RETURN_REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'excess_quantity', label: 'Excess Quantity' },
  { value: 'quality_issues', label: 'Quality Issues' },
  { value: 'other', label: 'Other' },
];

interface BillOption {
  id: string;
  billNumber: string;
}

interface ReturnableItem {
  id: string;
  label: string;
  billedQuantity: number;
  unitPrice: number;
  returnQuantity: number;
  returnReason: string;
}

export const PurchaseReturnCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [bills, setBills] = useState<BillOption[]>([]);
  const [selectedBill, setSelectedBill] = useState<BillOption | null>(null);
  const [items, setItems] = useState<ReturnableItem[]>([]);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [returnDescription, setReturnDescription] = useState('');
  const [loadingBills, setLoadingBills] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingBills(true);
        const response = await PurchaseService.getPurchaseBills({ limit: 100 } as any);
        const list = (response as any).purchase_bills || (response as any).items || [];
        setBills(list.map((b: any) => ({
          id: b.id,
          billNumber: b.bill_number || b.billNumber,
        })));
      } catch (error) {
        enqueueSnackbar('Failed to load purchase bills', { variant: 'error' });
      } finally {
        setLoadingBills(false);
      }
    })();
  }, []);

  const handleBillChange = async (bill: BillOption | null) => {
    setSelectedBill(bill);
    setItems([]);
    if (!bill) return;

    try {
      setLoadingItems(true);
      const data: any = await PurchaseService.getPurchaseBill(bill.id);
      const rawItems = data.items || [];
      setItems(rawItems.map((item: any) => ({
        id: item.id,
        label: item.variant?.product?.name || item.product_name || item.description || item.variant?.name || 'Item',
        billedQuantity: item.quantity,
        unitPrice: item.unit_price || item.unitPrice || 0,
        returnQuantity: 0,
        returnReason: 'defective',
      })));
    } catch (error) {
      enqueueSnackbar('Failed to load bill items', { variant: 'error' });
    } finally {
      setLoadingItems(false);
    }
  };

  const updateItem = (itemId: string, field: 'returnQuantity' | 'returnReason', value: number | string) => {
    setItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item)));
  };

  const totalReturnAmount = items.reduce(
    (sum, item) => sum + item.returnQuantity * item.unitPrice, 0
  );

  const handleSubmit = async () => {
    if (!selectedBill) {
      enqueueSnackbar('Select a purchase bill first', { variant: 'warning' });
      return;
    }
    const itemsToReturn = items.filter((item) => item.returnQuantity > 0);
    if (itemsToReturn.length === 0) {
      enqueueSnackbar('Enter a return quantity for at least one item', { variant: 'warning' });
      return;
    }
    for (const item of itemsToReturn) {
      if (item.returnQuantity > item.billedQuantity) {
        enqueueSnackbar(`Return quantity for ${item.label} cannot exceed billed quantity (${item.billedQuantity})`, { variant: 'error' });
        return;
      }
    }

    try {
      setSubmitting(true);
      const created = await PurchaseService.createPurchaseReturn({
        purchaseBillId: selectedBill.id,
        returnDate,
        returnDescription: returnDescription || undefined,
        items: itemsToReturn.map((item) => ({
          purchaseBillItemId: item.id,
          returnQuantity: item.returnQuantity,
          returnReason: item.returnReason,
        })),
      });
      enqueueSnackbar('Purchase return created successfully', { variant: 'success' });
      navigate(`/admin/purchase/returns/${created.id}`);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to create purchase return', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/purchase/returns')}>
          Back
        </Button>
        <Typography variant="h4">Create Purchase Return</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={bills}
              getOptionLabel={(b) => b.billNumber}
              loading={loadingBills}
              value={selectedBill}
              onChange={(_, value) => handleBillChange(value)}
              renderInput={(params) => <TextField {...params} label="Purchase Bill" required />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Return Date"
              type="date"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description (optional)"
              multiline
              rows={2}
              value={returnDescription}
              onChange={(e) => setReturnDescription(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loadingItems && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {!loadingItems && items.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Items</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Billed Qty</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Return Qty</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell align="right">{item.billedQuantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={item.returnQuantity}
                        onChange={(e) => updateItem(item.id, 'returnQuantity', Math.max(0, parseInt(e.target.value, 10) || 0))}
                        inputProps={{ min: 0, max: item.billedQuantity, style: { textAlign: 'right', width: 70 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={item.returnReason}
                          onChange={(e) => updateItem(item.id, 'returnReason', e.target.value)}
                        >
                          {RETURN_REASONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(item.returnQuantity * item.unitPrice)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Typography variant="h6">Total Return Amount: {formatCurrency(totalReturnAmount)}</Typography>
          </Box>
        </Paper>
      )}

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={() => navigate('/admin/purchase/returns')}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !selectedBill || items.every((i) => i.returnQuantity === 0)}
        >
          {submitting ? <CircularProgress size={24} /> : 'Create Return'}
        </Button>
      </Box>
    </Box>
  );
};
